import { useQuery } from '@tanstack/react-query';
import { companyByIdQueryOptions } from '@/features/companies/company-edit/company-detail-query';

interface WorkspaceBrandProps {
  /** The selected company/workspace id (the `?companyId=` search param). */
  companyId: string;
}

/**
 * Logo of the company (área de trabalho) the consultant entered, rendered next
 * to the page title on the internal pages (Programas, Vagas, Forms).
 *
 * - Loading → a muted placeholder square (keeps the header from jumping).
 * - Company with a logo → the logo image.
 * - Company without a logo (RN-02) → an initials avatar over a neutral fill,
 *   matching the fallback the Home cards already use.
 */
export function WorkspaceBrand({ companyId }: WorkspaceBrandProps) {
  const { data, isPending } = useQuery(companyByIdQueryOptions(companyId));

  if (isPending) {
    return <div aria-hidden="true" className="size-8 shrink-0 animate-pulse rounded-md bg-muted" />;
  }

  // Defensive: if the company failed to load we still render a neutral square
  // rather than nothing, so the title row stays visually balanced.
  const name = data?.name ?? '';
  const logoUrl = data?.logoUrl ?? null;
  const label = name ? `Logo de ${name}` : 'Logo da empresa';

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={label}
        className="size-8 shrink-0 rounded-md object-cover"
        data-testid="workspace-brand-logo"
      />
    );
  }

  return (
    <div
      aria-label={label}
      role="img"
      data-testid="workspace-brand-fallback"
      className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-sm font-medium text-foreground"
    >
      {name.charAt(0).toUpperCase() || '—'}
    </div>
  );
}
